import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, MessageCircleIcon, Plus, Minus } from "lucide-react";
import "./FAQ.css";

const FAQ = () => {
  const [openIndices, setOpenIndices] = useState([0]); // First item open by default, allow multiple

  const faqs = [
    {
      question: "What is TeamGrid?",
      answer:
        "TeamGrid is an AI-powered team intelligence platform that analyses real work patterns and productivity signals to generate summaries, insights, and automation recommendations for teams.",
    },
    {
      question: "Does TeamGrid invade privacy?",
      answer:
        "No, TeamGrid respects your privacy. We only collect work-related data with your consent and use it to provide insights. All data is encrypted and stored securely. You have full control over what data is shared.",
    },
    {
      question: "How does the tracking work?",
      answer:
        "TeamGrid uses non-intrusive tracking methods that analyze work patterns and productivity signals. We track application usage, time spent on tasks, and work patterns to generate insights without capturing sensitive information like passwords or personal data.",
    },
    {
      question: "Can I export reports?",
      answer:
        "Yes, you can export comprehensive reports in various formats including PDF, CSV, and Excel. Reports include team productivity metrics, insights, and recommendations that you can share with stakeholders.",
    },
    {
      question: "Can I cancel or change my subscription anytime?",
      answer:
        "Yes, you can cancel or change your subscription at any time. Changes will be reflected in your next billing cycle. There are no cancellation fees or long-term commitments required.",
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndices((prev) => {
      if (prev.includes(index)) {
        // Remove index if it's already open
        return prev.filter((i) => i !== index);
      } else {
        // Add index if it's closed
        return [...prev, index];
      }
    });
  };

  return (
    <div className="faq-container">
      <div className="faq-wrapper">
        {/* Got Questions Button */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800/80 backdrop-blur-sm shadow-lg">
            <MessageCircleIcon className="w-4 h-4 fill-teal-400" />
            <p className="text-xs tracking-[0.2em] uppercase text-teal-400 font-medium">
              Got Questions?
            </p>
          </div>
        </div>

        {/* Main Title */}
        <h2 className="faq-title">Frequently Asked Questions</h2>

        {/* Subtitle */}
        <p className="faq-subtitle">
          Everything you need to know about how TeamGrid works and the value it
          delivers.
        </p>

        {/* FAQ Items */}
        <div className="faq-items">
          {faqs.map((faq, index) => {
            const isOpen = openIndices.includes(index);
            return (
              <motion.div
                key={index}
                className={`faq-item ${isOpen ? "faq-item-open" : ""}`}
                initial={false}
                transition={{ duration: 0.3 }}
              >
                <div className="faq-item-content">
                  <button
                    className="faq-question-button"
                    onClick={() => toggleFAQ(index)}
                  >
                    <h3 className="faq-question">{faq.question}</h3>
                    <div className="faq-icon-wrapper">
                      {isOpen ? (
                        <Minus className="faq-icon faq-icon-minus" />
                      ) : (
                        <Plus className="faq-icon faq-icon-plus" />
                      )}
                    </div>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="faq-answer-wrapper"
                      >
                        <p className="faq-answer">{faq.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FAQ;
